/**
 * AISearchPanel.jsx
 * ==================
 * AI-powered semantic search across document content.
 * Uses ChromaDB for vector similarity search.
 */
import React, { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  Sparkles, 
  FileText, 
  Calendar,
  Tag,
  ChevronRight,
  Loader2,
  Brain,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AISearchPanel = ({ onDocumentSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchStats, setSearchStats] = useState(null);
  const [searchType, setSearchType] = useState('semantic'); // semantic | entity | hybrid

  // Perform AI search
  const performSearch = useCallback(async (e) => {
    e?.preventDefault();
    
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setIsSearching(true);
    setResults([]);

    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/document-ai/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: query.trim(),
          search_type: searchType,
          limit: 20,
          include_ai_insights: true
        })
      });

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setResults(data.results || []);
      setSearchStats({
        total: data.total_results || data.results?.length || 0,
        search_time_ms: data.search_time_ms,
        search_type: data.search_type
      });

    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [query, searchType]);

  // Format confidence score
  const formatConfidence = (score) => {
    if (!score && score !== 0) return null;
    const percentage = Math.round(score * 100);
    return (
      <span className={`text-xs ${
        percentage > 80 ? 'text-green-600' : 
        percentage > 60 ? 'text-yellow-600' : 'text-muted-foreground'
      }`}>
        {percentage}% match
      </span>
    );
  };

  // Get search type icon
  const getSearchTypeIcon = (type) => {
    switch (type) {
      case 'semantic': return Brain;
      case 'entity': return Tag;
      case 'hybrid': return Zap;
      default: return Search;
    }
  };

  return (
    <Card data-testid="ai-search-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle>AI Document Search</CardTitle>
        </div>
        <CardDescription>
          Search documents using AI-powered semantic understanding
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Search Type Toggle */}
        <div className="flex gap-2">
          {[
            { value: 'semantic', label: 'Semantic', icon: Brain, desc: 'Meaning-based search' },
            { value: 'entity', label: 'Entities', icon: Tag, desc: 'Search extracted data' },
            { value: 'hybrid', label: 'Hybrid', icon: Zap, desc: 'Combined search' }
          ].map(({ value, label, icon: Icon, desc }) => (
            <button
              key={value}
              onClick={() => setSearchType(value)}
              className={`flex-1 p-3 rounded-lg border transition-all ${
                searchType === value 
                  ? 'border-primary bg-primary/5 text-primary' 
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <Icon className="h-4 w-4 mx-auto mb-1" />
              <div className="text-sm font-medium">{label}</div>
              <div className="text-xs text-muted-foreground">{desc}</div>
            </button>
          ))}
        </div>

        {/* Search Input */}
        <form onSubmit={performSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                searchType === 'semantic' ? 'Search by meaning... e.g., "invoices from last month"' :
                searchType === 'entity' ? 'Search entities... e.g., "invoice_number:1234"' :
                'Search documents...'
              }
              className="pl-10"
              disabled={isSearching}
            />
          </div>
          <Button type="submit" disabled={isSearching || !query.trim()}>
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </form>

        {/* Search Stats */}
        {searchStats && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{searchStats.total} results</span>
            {searchStats.search_time_ms && (
              <span>{Math.round(searchStats.search_time_ms)}ms</span>
            )}
            <Badge variant="outline" className="text-xs">
              {searchStats.search_type || searchType} search
            </Badge>
          </div>
        )}

        {/* Results */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {results.length === 0 && !isSearching && query && (
            <div className="p-8 text-center text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No results found</p>
              <p className="text-xs mt-1">Try different keywords or search type</p>
            </div>
          )}

          {results.map((result, index) => {
            const Icon = getSearchTypeIcon(result.match_type);
            return (
              <div
                key={result.doc_id || index}
                className="p-3 border rounded-lg hover:border-primary/50 hover:bg-muted/30 cursor-pointer transition-all group"
                onClick={() => onDocumentSelect?.(result.doc_id)}
                data-testid={`search-result-${index}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="font-medium truncate">
                        {result.title || 'Untitled Document'}
                      </span>
                    </div>
                    
                    {/* AI Insights Preview */}
                    {result.ai_insights && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        {result.ai_insights.classification && (
                          <Badge variant="secondary" className="mr-2">
                            {result.ai_insights.classification.category}
                          </Badge>
                        )}
                        {result.ai_insights.entities?.vendor_name && (
                          <span className="text-xs">
                            Vendor: {result.ai_insights.entities.vendor_name}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Match Highlights */}
                    {result.highlights && result.highlights.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground italic">
                        "...{result.highlights[0]}..."
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                      <Badge variant="outline">{result.doc_type || 'Document'}</Badge>
                      {result.created_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(result.created_at).toLocaleDateString()}
                        </span>
                      )}
                      {formatConfidence(result.similarity_score || result.score)}
                    </div>
                  </div>

                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Search Tips */}
        {results.length === 0 && !isSearching && !query && (
          <div className="p-4 bg-muted/30 rounded-lg">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Search Tips
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Semantic:</strong> "Find contracts expiring soon"</li>
              <li>• <strong>Entity:</strong> "vendor:Sharma Electronics"</li>
              <li>• <strong>Hybrid:</strong> "invoices over ₹10,000 from February"</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AISearchPanel;
